import { ColumnDef } from '@tanstack/react-table'
import { compact } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import Combo from 'app/components/ui/combo/combo'
import { ContactCell } from '../shared/ContactCell'
import { LazyLocationCell } from '../LazyLocationCell'
import SimpleTable from 'app/components/table/SimpleTable'
import { convertUrlFiltersToES, FilterableColumn, FilterValueType } from 'app/filter.utils'
import { closeModal } from 'app/lib/modal'
import { tagFilterOptionsFetcher } from 'app/lib/fetchers/tagFilterOptionsFetcher'
import { useContactStore } from 'app/stores/StoreContext'
import { useWorkspace } from 'app/utils'
import type { ContactJson, StageType } from 'app/types/entities'
import type { OptionType } from 'types/OptionType'

/** Toggle a contact in/out of the selected list, keyed by id. Pure — unit tested. */
export function toggleContact(selected: ContactJson[], contact: ContactJson): ContactJson[] {
  return selected.some((c) => c.id === contact.id)
    ? selected.filter((c) => c.id !== contact.id)
    : [...selected, contact]
}

/** Compose the ES search: base scope + optional name wildcard + filter clauses. Pure — unit tested. */
export function buildContactSearch(base: string, query: string, esFilters: string[]): string {
  const q = query.trim()
  return compact([base, q ? `name:*${q}*` : ``, ...esFilters]).join(` AND `)
}

const CONTACT_FILTERS: FilterableColumn[] = [
  { attribute: `name`, displayName: `Name`, type: FilterValueType.string },
  {
    attribute: `locationId`,
    displayName: `Location`,
    optionsSource: `/api/locations`,
    type: FilterValueType.id,
    uniqOperator: `in`,
  } as FilterableColumn,
  {
    attribute: `tags`,
    displayName: `Tags`,
    fetcher: (params) => tagFilterOptionsFetcher(`Contact`, params),
    type: FilterValueType.stringArray,
  } as FilterableColumn,
  {
    attribute: `leadStatus.keyword`,
    displayName: `Lead Status`,
    type: FilterValueType.multiple,
    options: [`Needs Action`, `In Progress`, `Scheduled`, `Closed Won`, `Closed Lost`].map((o) => ({
      id: o,
      name: o,
      value: o,
    })),
  },
  {
    attribute: `lifecycleStage.keyword`,
    displayName: `Lifecycle Stage`,
    type: FilterValueType.multiple,
    options: [`Lead`, `Qualified Lead`, `New Patient Scheduled`, `Patient`].map((o) => ({ id: o, name: o, value: o })),
  },
  {
    attribute: `channel.keyword`,
    displayName: `Channel`,
    type: FilterValueType.asyncSelect,
    nonEmpty: true,
    optionsSource: `/api/channels`,
  },
  {
    attribute: `isQualified`,
    displayName: `Qualified`,
    type: FilterValueType.boolean,
    options: [
      { id: `qualified`, value: `true`, name: `Yes` },
      { id: `not_qualified`, value: `false`, name: `No` },
    ],
  },
  { attribute: `productionLtv`, displayName: `Net Production`, type: FilterValueType.number },
]

/** Column defs for the add-contacts table. Exported for UI regression specs. */
export function buildColumns(
  selectedIds: ReadonlySet<string | undefined>,
  onToggle: (contact: ContactJson) => void,
): ColumnDef<ContactJson, any>[] {
  return [
    {
      id: `select`,
      header: ``,
      size: 44,
      enableSorting: false,
      enableResizing: false,
      meta: {
        defaultVisible: true,
        ControlledComponent: (c?: ContactJson) =>
          c ? (
            <div className="flex h-full items-center justify-center">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-strong"
                checked={selectedIds.has(c.id)}
                onChange={() => onToggle(c)}
              />
            </div>
          ) : (
            <></>
          ),
      },
    },
    {
      id: `name`,
      header: `Name`,
      accessorKey: `name`,
      size: 220,
      meta: {
        defaultVisible: true,
        ControlledComponent: (c?: ContactJson) =>
          c ? (
            <div className="flex h-full items-center px-2">
              <ContactCell contact={c} />
            </div>
          ) : (
            <></>
          ),
      },
    },
    {
      id: `location`,
      header: `Location`,
      size: 160,
      enableSorting: false,
      meta: {
        defaultVisible: true,
        ControlledComponent: (c?: ContactJson) =>
          c?.locationId ? (
            <div className="px-2">
              <LazyLocationCell locationId={c.locationId} className="text-body-xs text-subtle" />
            </div>
          ) : (
            <span className="px-2 text-body-xs text-minimal">—</span>
          ),
      },
    },
    { id: `leadStatus`, header: `Lead status`, accessorKey: `leadStatus`, size: 140, meta: { defaultVisible: true } },
    {
      id: `tags`,
      header: `Tags`,
      size: 200,
      enableSorting: false,
      meta: {
        defaultVisible: true,
        ControlledComponent: (c?: ContactJson) => (
          <span className="px-2 text-body-xs text-subtle">{(c?.tags ?? []).join(`, `) || `—`}</span>
        ),
      },
    },
  ]
}

export function AddContactsModal({
  pipeline,
  onAdd,
  defaultStageId,
  excludeSearch,
}: {
  // A pipeline, or a List (whose stages carry an id): only its id and stages are read.
  pipeline: { id?: string | null; stages?: readonly StageType[] | null }
  onAdd: (contacts: ContactJson[], stage: StageType) => void
  /** The stage the picker starts on. Defaults to the first stage by order. */
  defaultStageId?: string
  /** A search term that keeps people out of the list. Defaults to leaving out whoever is on this pipeline already. */
  excludeSearch?: string
}): ReactElement {
  const workspace = useWorkspace()
  const contactStore = useContactStore()
  const stages = useMemo(() => [...(pipeline.stages ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [pipeline])
  const stageOptions = useMemo<OptionType[]>(
    () => stages.map((s) => ({ id: s.name, value: s.name, name: s.name })),
    [stages],
  )

  const [query, setQuery] = useState(``)
  const [encodedFilters, setEncodedFilters] = useState(``)
  const [data, setData] = useState<ContactJson[]>([])
  const [selected, setSelected] = useState<ContactJson[]>([])
  const [stageName, setStageName] = useState<string>(
    (stages.find((candidate) => candidate.id === defaultStageId) ?? stages[0])?.name ?? ``,
  )
  const stage = stages.find((s) => s.name === stageName)
  const selectedIds = useMemo(() => new Set(selected.map((c) => c.id)), [selected])

  useEffect(() => {
    let active = true
    const handle = setTimeout(async () => {
      // Quoted: a reserved id carries a colon, which Lucene reads as a field
      // separator. Harmless for a hex id, essential for a system one.
      const exclude = excludeSearch ?? `!pipelines.id:"${pipeline.id}"`
      const base = [`workspaceId:${workspace?.id ?? ``}`, exclude].filter(Boolean).join(` AND `)
      const esFilters = convertUrlFiltersToES(encodedFilters, `and`, CONTACT_FILTERS)
      const search = buildContactSearch(base, query, esFilters)
      const result = await contactStore.fetchList({ search, pagination: { limit: 50 } })
      if (!active) return
      setData(((result.data as any)?.contacts?.hits as ContactJson[]) ?? [])
    }, 300)
    return () => {
      active = false
      clearTimeout(handle)
    }
  }, [query, encodedFilters, workspace, pipeline.id, excludeSearch, contactStore])

  const columns = useMemo<ColumnDef<ContactJson, any>[]>(
    () => buildColumns(selectedIds, (c) => setSelected((prev) => toggleContact(prev, c))),
    [selectedIds],
  )

  return (
    <div className="flex h-[min(640px,85vh)] w-[min(960px,90vw)] flex-col">
      <div className="min-h-0 flex-1 overflow-hidden px-4 pt-2">
        <SimpleTable
          data={data}
          columns={columns}
          filterableColumns={CONTACT_FILTERS}
          enableDeepLinking={false}
          onSearchValueChange={setQuery}
          onFiltersChange={setEncodedFilters}
          initialSort={[{ id: `name`, desc: false }]}
          noDataMessage="No contacts found"
          hideFooter
          showColumnOrdering={false}
        />
      </div>
      <div className="flex shrink-0 items-center justify-between border-t border-default p-4">
        <span className="text-body-sm text-default">{selected.length} selected</span>
        <div className="flex items-center gap-x-3">
          <div className="flex items-center gap-x-2">
            <span className="text-ui-xs text-default">Add to stage</span>
            <div className="w-40">
              <Combo
                options={stageOptions}
                value={stageOptions.find((o) => o.value === stageName)}
                onChange={(opt) => setStageName((opt?.value as string) || stageName)}
                useSingleFieldInput={false}
                placeholder="Select a stage"
                inputClassName="!h-9 !rounded-md !px-3 !py-0 !shadow-none !text-body-sm"
                chevronClassName="!h-4 !w-4 !text-minimal"
                optionsContainerClassName="py-1"
              />
            </div>
          </div>
          <button className="btn btn-secondary btn-md" onClick={closeModal}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-md"
            disabled={selected.length === 0 || !stage}
            onClick={() => {
              if (stage) onAdd(selected, stage)
              closeModal()
            }}
          >
            {selected.length ? `Add ${selected.length}` : `Add`}
          </button>
        </div>
      </div>
    </div>
  )
}
